/* Built In Imports */
import Head from 'next/head';
import { useContext, useEffect, useState } from 'react';

/* External Imports */
import { Box, Icon, Image, Link, Text, useMediaQuery } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import parser from 'html-react-parser';
import filter from 'lodash/filter';

/* Internal Imports */
/* Components */
import LazyLoadImageComponent from '@components/UI/LazyLoadImageComponent';
import { getCookie } from '@components/Utility/utils';
import config from '@config';
import { AuthContext } from '@store/auth-context';

/* Styles */
import classes from '@styles/components/BottomWidget.module.css';

/**
 * Renders the Bottom widget component
 *
 * @param {object} popUpData - content from api
 * @param {string} region - region and country
 * @returns {ReactElement} Bottom widget component
 */
const BottomWidget = ({
  region,
  lang,
  url,
  popUpData,
  popUpBlackListedJson,
  popUpFullData,
}) => {
  const authContext = useContext(AuthContext);
  const bottomWidgetCookie = getCookie('bottom_widget');
  const [isClose, setIsClose] = useState(true);
  const [showText, setShowText] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isMobile] = useMediaQuery('(max-width: 590px)');

  useEffect(() => {
    let finalUrls = [];
    const timer = setTimeout(() => {
      if (
        popUpBlackListedJson.length &&
        popUpBlackListedJson[0]?.blacklisted_url?.length
      ) {
        finalUrls = filter(
          popUpBlackListedJson[0]?.blacklisted_url,
          urlObj => urlObj?.url === url?.split('?')[0]?.split('#')[0]
        );
        if (!finalUrls.length) {
          finalUrls = filter(
            popUpBlackListedJson[0]?.blacklisted_starts_with,
            urlObj => url?.startsWith(urlObj?.url)
          );
        }
      }

      if (
        popUpData?.status === 'failure' ||
        bottomWidgetCookie === 'false' ||
        finalUrls?.length
      ) {
        setIsClose(true);
      } else if (
        !finalUrls.length &&
        Array.isArray(popUpData) &&
        popUpData.length > 0
      ) {
        setIsClose(false);
        if (bottomWidgetCookie === undefined) {
          authContext.setBottomWidgetCookie(true);
        }
      } else if (
        popUpBlackListedJson.length &&
        Array.isArray(popUpBlackListedJson[0]?.blacklisted_url)
      ) {
        setIsClose(false);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [popUpData, authContext, popUpBlackListedJson, bottomWidgetCookie, url]);

  useEffect(() => {
    if (
      !(
        (bottomWidgetCookie &&
          bottomWidgetCookie === 'false' &&
          authContext.region === 'in') ||
        isClose
      ) &&
      isMobile &&
      popUpFullData?.template === 'mom-ipl'
    ) {
      document.body.style.height = 'calc(var(--viewport-height) - 55px)';
      document.body.style.overflow = 'hidden';
    }
    if (isMobile && popUpFullData?.popup?.[0]?.bgVideo) {
      if (isVideoLoaded) {
        const textTimer = setTimeout(() => {
          setShowText(true);
        }, 1000);
        return () => clearTimeout(textTimer);
      }
    } else {
      const textTimer = setTimeout(() => {
        setShowText(true);
      }, 1000);
      return () => clearTimeout(textTimer);
    }
  }, [isMobile, isVideoLoaded, popUpFullData]);

  const WidgetHandler = () => {
    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    setIsClose(true);
    authContext.setBottomWidgetCookie(false);
  };

  const fadeInUp = keyframes`
    from {
      opacity: 0;
      transform: translateY(100%);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  `;

  useEffect(() => { 
      visualViewport.addEventListener('resize', () => {
        document.documentElement.style.setProperty(
          '--viewport-height',
          `${visualViewport.height}px`
        );
      }); 
  }, []);

  // console.log('==>', popUpFullData?.popup);

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, viewport-fit=cover"
        />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css?family=Geist|Trirong:ital|Trirong|Noto+Sans+Tamil"
        />
      </Head>
      {popUpFullData?.template === 'mom-ipl' && (
        <Box
          pos="fixed"
          w="100%"
          height="100%"
          overflowY="hidden"
          bottom={0}
          bgColor="rgba(0, 0, 0, 0.55)"
          transform="translate3d(0,0,0)"
          zIndex={9999}
          style={{ bottom: '0' }}
          display={{
            base:
              (bottomWidgetCookie &&
                bottomWidgetCookie === 'false' &&
                authContext.region === 'in') ||
              isClose
                ? 'none'
                : 'inherit',
            sm: 'none',
          }}
        >
          <Box
            className="popup-component"
            position="fixed"
            transform="translate3d(0,0,0)"
            maxWidth="590px"
            width="100%"
            height="346px"
            left="0"
            bottom="0"
            display="flex"
            flexDirection="column"
            alignItems="center"
            paddingBottom="0"
          >
            <Icon
              width="8"
              height="8"
              borderRadius="100%"
              bgColor="#000"
              position="fixed"
              right="10px"
              bottom="400px"
              onClick={() => {
                WidgetHandler();
              }}
              cursor="pointer"
            >
              <svg fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M8 8L16 16M16 8L8 16"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="square"
                />
              </svg>
            </Icon>
            {isMobile && (
              <Box
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: 'calc(390px + env(safe-area-inset-bottom))',
                  bottom: 0,
                  zIndex: -1,
                  borderRadius: '20px 20px 0 0',
                  background:
                    popUpFullData?.popup?.[0]?.fallbackBgColor || '#000',
                  backgroundImage: popUpFullData?.popup?.[0]?.bannerImage?.url
                    ? `url(${popUpFullData.popup[0].bannerImage?.url})`
                    : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'top center',
                }}
              />
            )}
            {showText && (
              <>
                {/* <Box
                  textAlign="right"
                  display="flex"
                  justifyContent="end"
                  width="100%"
                  paddingRight="20px"
                  position="absolute"
                  top=" -22px"
                >
                  <Image
                    src="https://static.sadhguru.org/d/46272/1746248029-logo.png"
                    alt=""
                    width="40px"
                  />
                </Box> */}
                <Box
                  className="popup-button-wrapper"
                  width="100%"
                  // height="110px"
                  overflow="hidden"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  my="30px"
                >
                  <Image
                    src={popUpFullData?.popup?.[0]?.bgImage}
                    maxW="115px"
                    w="100%"
                    animation={`${fadeInUp} 0.9s ease-in-out forwards`}
                  />
                </Box>

                <Box className="popup-section-wrapper">
                  <Box className="popup-text">
                    {' '}
                    {popUpFullData?.popup?.[0]?.title}
                  </Box>
                  <Box className="popup-text">
                    {' '}
                    {popUpFullData?.popup?.[0]?.subTitle}
                  </Box>
                  <Box className="popup-text">
                    {' '}
                    {popUpFullData?.popup?.[0]?.subTitle2}
                  </Box>
                </Box>

                <Box
                  className="popup-section-wrapper"
                  width="100%"
                  height="40px"
                  overflow="hidden"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Text
                    className="popup-footer popup-animate"
                    animation={`${fadeInUp} 0.9s ease-in-out forwards`}
                    fontFamily="'Geist', sans-serif"
                    fontWeight="400"
                    fontSize="16px"
                    lineHeight="110%"
                    letterSpacing="-0.01em"
                    color="rgba(255, 255, 255, 1)"
                    textAlign="center"
                  >
                    {popUpFullData?.popup?.[0]?.bottomText}
                  </Text>
                </Box>
                <Box
                  className="popup-button-wrapper"
                  width="100%"
                  height="48px"
                  overflow="hidden"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Box
                    className="popup-button-container popup-animate"
                    animation={`${fadeInUp} 0.9s ease-in-out forwards`}
                  >
                    <Link
                      className="popup-button"
                      padding="10px 70px"
                      bg="#fff"
                      borderRadius="35px"
                      fontFamily="'Trirong', serif"
                      fontWeight="500"
                      fontSize="16px"
                      color="#ec1c2d"
                      border="none"
                      cursor="pointer"
                      target="_blank"
                      fontStyle="italic"
                      href={popUpFullData?.popup?.[0]?.buttonCtaLink}
                    >
                      {popUpFullData?.popup?.[0]?.buttonCtaText}{' '}
                      <Box
                        fontFamily="FedraSansStd-book"
                        as="span"
                        fontWeight="bold"
                        fontStyle="normal"
                      >
                        {popUpFullData?.popup?.[0]?.buttonCtaText2}
                      </Box>
                    </Link>
                  </Box>
                </Box>
                <Box
                  className="popup-section-wrapper"
                  width="100%"
                  height="40px"
                  overflow="hidden"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  gap="4px"
                >
                  <Text
                    className="popup-footer popup-animate"
                    animation={`${fadeInUp} 0.9s ease-in-out forwards`}
                    fontFamily="'Geist', sans-serif"
                    fontWeight="400"
                    fontSize="14px"
                    lineHeight="110%"
                    letterSpacing="-0.01em"
                    color="rgba(255, 255, 255, 1)"
                    textAlign="center"
                  >
                    {popUpFullData?.popup?.[0]?.bottomText2}
                  </Text>

                  <Image
                    src="https://static.sadhguru.org/d/46272/1746248029-icon-stars-4-9-1.png"
                    alt=""
                    width="70px"
                  />
                </Box>
              </>
            )}
          </Box>

          <style>
            {`
    @keyframes slideInUpOut {

  0%   { opacity: 0; transform: translateY(100%); }
  10%  { opacity: 1; transform: translateY(0); }

  30%  { opacity: 1; transform: translateY(0); }
 
  40%  { opacity: 0; transform: translateY(-100%); }

  100% { opacity: 0; transform: translateY(-100%); }
}

.popup-section-wrapper {
  position: relative;
  width: 100%;
  height: 40px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.popup-text {
  position: absolute;
  opacity: 0;
  font-family: 'FedraSansStd-medium', sans-serif;
  font-size: 28px;
  line-height: normal;
  letter-spacing: -0.06em;
  text-align: center;
  background: linear-gradient(270deg, #ec2224 0%, #636eff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: slideInUpOut 6s ease-in-out infinite both;
}

.popup-text:nth-child(1) { animation-delay: 0s; }
.popup-text:nth-child(2) { animation-delay: 2s; }
.popup-text:nth-child(3) { animation-delay: 4s; }
    `}{' '}
          </style>
        </Box>
      )}
      {popUpFullData?.template === 'mom' && (
        <Box
          pos="fixed"
          w="100%"
          height="100%"
          overflowY="auto"
          top={0}
          bgColor="rgba(0, 0, 0, 0.55)"
          zIndex={9999}
          style={{ bottom: 'env(safe-area-inset-bottom)' }}
          display={{
            base:
              (bottomWidgetCookie &&
                bottomWidgetCookie === 'false' &&
                authContext.region === 'in') ||
              isClose
                ? 'none'
                : 'inherit',
            sm: 'none',
          }}
        >
          <Box
            className="popup-component"
            position="fixed"
            maxWidth="590px"
            width="100%"
            height="346px"
            left="0"
            bottom="env(safe-area-inset-bottom)"
            display="flex"
            flexDirection="column"
            alignItems="center"
            paddingBottom={isMobile ? 'env(safe-area-inset-bottom)' : '0'}
          >
            <Icon
              width="8"
              height="8"
              borderRadius="100%"
              bgColor="#000"
              position="absolute"
              right="10px"
              top="-90px"
              onClick={() => {
                WidgetHandler();
              }}
              cursor="pointer"
            >
              <svg fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M8 8L16 16M16 8L8 16"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="square"
                />
              </svg>
            </Icon>
            {isMobile ? (
              popUpFullData?.popup?.[0]?.bgVideo ? (
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  onLoadedData={() => setIsVideoLoaded(true)}
                  style={{
                    position: 'absolute',
                    width: '100%',
                    height: 'calc(390px + env(safe-area-inset-bottom))',
                    bottom: 0,
                    objectFit: 'cover',
                    zIndex: -1,
                    borderRadius: '20px 20px 0 0',
                  }}
                >
                  <source
                    src={popUpFullData?.popup?.[0]?.bgVideo}
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <Box
                  style={{
                    position: 'absolute',
                    width: '100%',
                    height: 'calc(390px + env(safe-area-inset-bottom))',
                    bottom: 0,
                    zIndex: -1,
                    borderRadius: '20px 20px 0 0',
                    background:
                      popUpFullData?.popup?.[0]?.fallbackBgColor || '#000',
                    backgroundImage: popUpFullData?.popup?.[0]?.fallbackImage
                      ? `url(${popUpFullData.popup[0].fallbackImage})`
                      : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                />
              )
            ) : (
              popUpFullData?.popup?.[0]?.bgVideo && (
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  style={{
                    position: 'absolute',
                    width: '100%',
                    height: '390px',
                    bottom: 0,
                    objectFit: 'cover',
                    zIndex: -1,
                    borderRadius: '20px 20px 0 0',
                  }}
                >
                  <source
                    src={popUpFullData?.popup?.[0]?.bgVideo}
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>
              )
            )}
            {showText && (
              <>
                <Box
                  className="popup-section-wrapper"
                  width="100%"
                  height="30px"
                  overflow="hidden"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Text
                    className="popup-title popup-animate"
                    animation={`${fadeInUp} 0.9s ease-in-out forwards`}
                    fontFamily="'FedraSansStd-medium', sans-serif"
                    fontSize="24px"
                    lineHeight="110%"
                    letterSpacing="-0.02em"
                    color="#fff"
                    textAlign="center"
                  >
                    {popUpFullData?.popup?.[0]?.title}
                  </Text>
                </Box>
                <Box
                  className="popup-section-wrapper"
                  width="100%"
                  height="50px"
                  overflow="hidden"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Text
                    className="popup-subtitle popup-animate"
                    animation={`${fadeInUp} 0.9s ease-in-out forwards`}
                    fontFamily="'FedraSansStd-medium', sans-serif"
                    fontSize="36px"
                    lineHeight="normal"
                    letterSpacing="-0.04em"
                    textAlign="center"
                    bgGradient="linear(270deg, #ec2224 0%, #636eff 100%)"
                    bgClip="text"
                  >
                    {popUpFullData?.popup?.[0]?.subTitle}
                  </Text>
                </Box>
                <Box
                  className="popup-button-wrapper"
                  width="100%"
                  height="140px"
                  overflow="hidden"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  my="20px"
                >
                  <Image
                    src={popUpFullData?.popup?.[0]?.bannerImage?.url}
                    alt={
                      popUpFullData?.popup?.[0]?.bannerImage?.alt ||
                      'Popup Image'
                    }
                    maxW="150px"
                    w="100%"
                    animation={`${fadeInUp} 0.9s ease-in-out forwards`}
                  />
                </Box>
                <Box
                  className="popup-button-wrapper"
                  width="100%"
                  height="48px"
                  overflow="hidden"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Box
                    className="popup-button-container popup-animate"
                    animation={`${fadeInUp} 0.9s ease-in-out forwards`}
                  >
                    <Link
                      className="popup-button"
                      padding="10px 70px"
                      bg="#fff"
                      borderRadius="35px"
                      fontFamily="'Trirong', serif"
                      fontWeight="500"
                      fontSize="16px"
                      color="#ec1c2d"
                      border="none"
                      cursor="pointer"
                      target="_blank"
                      fontStyle="italic"
                      href={popUpFullData?.popup?.[0]?.buttonCtaLink}
                    >
                      {popUpFullData?.popup?.[0]?.buttonCtaText}{' '}
                      <Box
                        fontFamily="FedraSansStd-book"
                        as="span"
                        fontWeight="bold"
                        fontStyle="normal"
                      >
                        {popUpFullData?.popup?.[0]?.buttonCtaText2}
                      </Box>
                    </Link>
                  </Box>
                </Box>
                <Box
                  className="popup-section-wrapper"
                  width="100%"
                  height="40px"
                  overflow="hidden"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Text
                    className="popup-footer popup-animate"
                    animation={`${fadeInUp} 0.9s ease-in-out forwards`}
                    fontFamily="'Geist', sans-serif"
                    fontWeight="400"
                    fontSize="14px"
                    lineHeight="110%"
                    letterSpacing="-0.01em"
                    color="rgba(255, 255, 255, 1)"
                    textAlign="center"
                  >
                    {popUpFullData?.popup?.[0]?.bottomText}
                  </Text>
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}
      {popUpFullData?.template !== 'mom' &&
        popUpFullData?.template !== 'mom-ipl' &&
        popUpData && (
          <Box
            float="left"
            w="auto"
            display={
              (bottomWidgetCookie &&
                bottomWidgetCookie === 'false' &&
                authContext.region === 'in') ||
              isClose
                ? 'none'
                : 'inherit'
            }
          >
            <Box
              pos="fixed"
              display={{ base: 'block', sm: 'block', md: 'block', lg: 'block' }}
              w={{ base: '84%', md: '307px', lg: '307px' }}
              borderRadius="7px 7px 0 0"
              boxShadow="0 8px 9px 0 rgb(0 0 0 / 50%)"
              textAlign="center"
              // Offset from bottom using safe area inset
              bottom="env(safe-area-inset-bottom)"
              right={{ base: '0', sm: '120px' }}
              bgColor={popUpData?.[0]?.backgroundColor?.hex}
              zIndex="99"
              minHeight={{ base: '105px', md: 'auto' }}
              left={{ base: '8%', md: 'inherit' }}
              style={{
                paddingBottom: isMobile ? 'env(safe-area-inset-bottom)' : 0,
              }}
            >
              <Box
                pos="absolute"
                top="-13px"
                right="-5px"
                bgImage={`url('${config.imgPath}/d/46272/1663645892-ion-android-cancel-ionicons.png')`}
                w="30px"
                h="30px"
                bgRepeat="no-repeat"
                bgPosition="center"
                bgSize="30px"
                cursor="pointer"
                borderRadius="100%"
                display={{ base: 'none', md: 'none' }}
                onClick={() => {
                  WidgetHandler();
                }}
              >
                &nbsp;
              </Box>
              <Box
                w={{ base: 'unset', md: 'unset' }}
                h={{ base: '186px', md: '186px' }}
                m="14px"
                float={{ base: 'none', md: 'left' }}
                borderRadius={{ base: '0px 0px 0px 0px', md: 'inherit' }}
                alignSelf={{
                  base: 'unset',
                  sm: 'unset',
                  md: 'unset',
                  lg: 'unset',
                }}
                className={classes['bengaluru-popup-img']}
              >
                <Box display={{ base: 'block', md: 'block', lg: 'block' }}>
                  <Link href={popUpData?.[0]?.buttonCtaLink}>
                    <LazyLoadImageComponent
                      alt={popUpData?.[0]?.bannerImage?.alt}
                      title={
                        popUpData?.[0]?.bannerImage?.title ||
                        popUpData?.[0]?.title
                      }
                      data-entity-type="file"
                      data-entity-uuid="3745e261-c945-4fcd-85de-99a4c6f5e4a7"
                      src={popUpData?.[0]?.bannerImage?.url}
                      textAlign="center"
                    />
                  </Link>
                </Box>
                <Box
                  display={{ base: 'none', sm: 'none', md: 'none', lg: 'none' }}
                  w="85%"
                >
                  {parser(popUpData?.[0]?.mobileCtaText || '')}
                </Box>
              </Box>

              <Box
                float="left"
                w={{ base: '100%', md: '100%' }}
                bgColor="#000000"
                m={{ base: '0 auto' }}
                p={{ base: '0 15px', md: '0 20px' }}
                mb="10px"
                alignSelf={{
                  base: 'unset',
                  sm: 'unset',
                  md: 'unset',
                  lg: 'unset',
                }}
              >
                <Link
                  isExternal
                  float={{ base: 'left', md: 'left' }}
                  w={{ base: '174px', sm: '174px', md: '174px', lg: '174px' }}
                  h={{ base: '47px', md: '32px' }}
                  fontFamily="FedraSansStd-medium"
                  fontSize="14px"
                  textAlign="center"
                  color={popUpData?.[0]?.buttonTextColor?.hex}
                  p="0"
                  lineHeight={{ base: '45px', md: '32px' }}
                  textDecoration="none !important"
                  bgColor={popUpData?.[0]?.buttonBackgroundColor?.hex}
                  borderRadius="5px"
                  mt={{ base: '0', md: '0' }}
                  mb={{ base: '10px' }}
                  href={popUpData?.[0]?.buttonCtaLink}
                  display={{ base: 'block' }}
                >
                  {popUpData?.[0]?.buttonCtaText}
                </Link>
                <Link
                  w="70px"
                  fontFamily="FedraSansStd-book"
                  fontSize="16px"
                  fontStyle="normal"
                  letterSpacing="normal"
                  textAlign="right"
                  float="right"
                  color="#ffffff !important"
                  p={{ base: '10px 24px 10px 0', md: '4px 0' }}
                  cursor="pointer"
                  d={{ base: 'inherit', md: 'inherit' }}
                  onClick={() => {
                    WidgetHandler();
                  }}
                  textDecoration="none !important"
                  display={{ base: 'block', md: 'block' }}
                >
                  Close
                </Link>
              </Box>
            </Box>
            <style jsx global>{`
              .popup-widget {
                float: left;
                text-align: left;
                color: #fff;
                font-size: 16px;
              }
              .popup-widget-span {
                font-size: 18px;
              }
              .popup-widget-bold {
                font-size: 18px;
              }
            `}</style>
          </Box>
        )}
    </>
  );
};

export default BottomWidget;
